import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, Transition as _Transition, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "relative mt-6" }
const _hoisted_2 = { class: "flex items-center gap-x-1 leading-normal" }
const _hoisted_3 = { class: "font-medium" }
const _hoisted_4 = { class: "text-gray-500" }
const _hoisted_5 = { class: "flex items-center gap-x-1 leading-normal" }
const _hoisted_6 = { class: "font-medium" }
const _hoisted_7 = { class: "text-gray-500" }
const _hoisted_8 = { class: "flex grow justify-between max-w-full min-w-0 items-center gap-x-2 pr-5" }
const _hoisted_9 = { class: "whitespace-nowrap truncate min-w-0" }
const _hoisted_10 = {
  key: 0,
  class: "text-gray-500"
}
const _hoisted_11 = {
  key: 0,
  class: "text-gray-500 text-sm whitespace-nowrap"
}
const _hoisted_12 = {
  key: 0,
  class: "sticky bottom-0 bg-white py-3 px-4 border-t"
}
const _hoisted_13 = { class: "text-red-500 text-right font-medium" }
const _hoisted_14 = { class: "relative" }
const _hoisted_15 = { class: "flex items-center gap-x-1 leading-normal" }
const _hoisted_16 = { class: "font-medium" }
const _hoisted_17 = { class: "text-gray-500" }
const _hoisted_18 = { class: "flex items-center gap-x-4 justify-end mt-6" }
const _hoisted_19 = { class: "default-tabs" }
const _hoisted_20 = { class: "space-x-8 flex border-b border-black border-opacity-10" }
const _hoisted_21 = {
  key: 0,
  class: "flex"
}
const _hoisted_22 = {
  key: 1,
  class: "flex"
}

import { computed, nextTick, onMounted, Ref, ref, watch } from 'vue';
import { ChevronDown, Plus } from 'lucide-vue-next';
import { Tab, Tabs } from '@makeabledk/vue-ui/components/tabs';
import { useIsFetching, useQueryClient } from '@tanstack/vue-query';
import { format, parseISO, subMonths } from 'date-fns';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';
import { cloneDeep } from 'lodash-es';
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { queryKeys, StudyPeriodDashboardPayload } from '@/plugins/store/actions/queries/study-periods/dashboards';
import { useStudyPeriodCategories, useStudyPeriodGrowthReferences, useStudyPeriods, useStudyPeriodsFavorites } from '@/plugins/store/actions/queries/study-periods';
import CustomMultiSelect from '@/components/ui/CustomMultiSelect.vue';
import TableTab from './TableTab.vue';
import ChartTab from './ChartTab.vue';
import CategoryPicker from './CategoryPicker.vue';
import TeamsPicker from './TeamsPicker.vue';
import Button from '@/components/ui/button/Button.vue';
import Spinner from '@/components/ui/Spinner.vue';
import SaveAnalysisDialog from './SaveAnalysisDialog.vue';
import SavedAnalysesDialog from './SavedAnalysesDialog.vue';
import ExpandCollapseTransition from '@/components/common/ExpandCollapseTransition.vue';
import { StudyPeriodSetup } from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'Index',
  setup(__props) {

const isFetching = useIsFetching({ queryKey: queryKeys.getDashboardRoot });
const isLoading = computed(() => isFetching.value > 0);

const animalGroupOptions = computed(() => [
    {
        value: '1',
        label: i18n.global.t('views.monitoring.teamComparison.fields.animalGroup.options.weaners'),
    },
    {
        value: '2',
        label: i18n.global.t('views.monitoring.teamComparison.fields.animalGroup.options.slaughterPigs'),
    },
    {
        value: '3',
        label: i18n.global.t('views.monitoring.teamComparison.fields.animalGroup.options.frats'),
    },
]);
const selectedAnimalGroup = ref<string | null>(null);

const { data: studyPeriodCategories } = useStudyPeriodCategories();

const isStudyPeriodFavoritesQueryEnabled = computed(() => selectedAnimalGroup.value !== null);
const { data: studyPeriodFavorites, isLoading: isStudyPeriodFavoritesLoading } = useStudyPeriodsFavorites({ animalGroupId: selectedAnimalGroup as Ref<string> }, { enabled: isStudyPeriodFavoritesQueryEnabled });

const isGrowthReferencesQueryEnabled = computed(() => selectedAnimalGroup.value !== null);
const { data: growthReferences, isLoading: isGrowthReferencesLoading } = useStudyPeriodGrowthReferences({ animalGroupId: selectedAnimalGroup as Ref<string> }, { enabled: isGrowthReferencesQueryEnabled });

const selectedStudyPeriodFavoriteIdsState = ref<number[]>([]);
const selectedStudyPeriodFavoriteIds = computed({
    get() {
        return selectedStudyPeriodFavoriteIdsState.value.filter((f) => studyPeriodFavorites.value?.some((f2) => f2.id === f));
    },
    set(value) {
        selectedStudyPeriodFavoriteIdsState.value = value;
    },
});

const selectedGrowthReferenceIdState = ref<number | null>(null);
const selectedGrowthReferenceId = computed({
    get() {
        return selectedGrowthReferenceIdState.value;
    },
    set(value) {
        selectedGrowthReferenceIdState.value = value;
    },
});

const selectedComparisonType = ref<'within' | 'between'>('between');

const selectedCategoryOptionsState = ref<number[]>([]);
const selectedCategoryOptions = computed({
    get() {
        return selectedCategoryOptionsState.value.filter((c) => studyPeriodCategories.value?.some((c2) => c2.options.some((o) => o.id === c)));
    },
    set(value) {
        selectedCategoryOptionsState.value = value;
    },
});

const selectedStudyPeriodsState = ref<number[]>([]);
const selectedStudyPeriods = computed({
    get() {
        return selectedStudyPeriodsState.value.filter((s) => studyPeriods.value?.some((s2) => s2.id === s));
    },
    set(value) {
        selectedStudyPeriodsState.value = value;
    },
});
const selectedFromDate = ref<string>(subMonths(new Date(), 3).toISOString());
const selectedToDate = ref<string>(new Date().toISOString());

function handleGrowthReferenceChange(id: number) {
    selectedGrowthReferenceId.value = id === selectedGrowthReferenceId.value ? null : id;
}

const studyPeriodsFetchParams = computed(() => ({
    animalGroupId: selectedAnimalGroup.value!,
    from: format(new Date(selectedFromDate.value), 'yyyy-MM-dd'),
    to: format(new Date(selectedToDate.value), 'yyyy-MM-dd'),
}));
const isStudyPeriodsQueryEnabled = computed(() => selectedAnimalGroup.value !== null);
const { data: studyPeriods, isLoading: isStudyPeriodsLoading } = useStudyPeriods(studyPeriodsFetchParams, { enabled: isStudyPeriodsQueryEnabled });

const animalGroupName = computed(() => animalGroupOptions.value.find((o) => o.value === selectedAnimalGroup.value)?.label || '');
const categoryNames = computed(
    () => studyPeriodCategories.value?.filter((c) => selectedCategoryOptions.value.includes(c.id)).flatMap((c) => c.options.filter((o) => selectedCategoryOptions.value.includes(o.id)).map((o) => o.name)) || []
);
const studyPeriodNames = computed(() => studyPeriods.value?.filter((s) => selectedStudyPeriods.value.includes(s.id)).map((s) => s.name) || []);
const favoriteTeamNames = computed(() => studyPeriodFavorites.value?.filter((o) => selectedStudyPeriodFavoriteIds.value.includes(o.id)).map((o) => o.name) || []);
const growthReferenceName = computed(() => growthReferences.value?.find((o) => o.id === selectedGrowthReferenceId.value)?.name || '');
const saveAnalysisState = computed(() => ({
    comparisonType: selectedComparisonType.value,
    categoryIds: selectedCategoryOptions.value,
    animalGroupId: selectedAnimalGroup.value ? parseInt(selectedAnimalGroup.value) : undefined,
    studyPeriodIds: selectedStudyPeriods.value,
    favoriteTeamIds: selectedStudyPeriodFavoriteIds.value,
    growthReferenceId: selectedGrowthReferenceId.value ?? undefined,
}));

const selectedChartCategoriesCount = computed(() => studyPeriodCategories.value?.filter((c) => c.options.some((o) => selectedCategoryOptions.value.includes(o.id) && o.type === 'chart')).length || 0);

const teamsPickerErrorMessage = computed(() => {
    const selectedStudyPeriodsCount = selectedStudyPeriods.value.length;
    const hasChartCategorySelected = studyPeriodCategories.value?.some((c) => c.options.some((o) => selectedCategoryOptions.value.includes(o.id) && o.type === 'chart'));

    if (selectedComparisonType.value === 'between' && selectedStudyPeriodsCount > 4 && hasChartCategorySelected) {
        return i18n.global.t('views.monitoring.teamComparison.fields.teamsPicker.errorMessages.tooManyTeamsSelectedBetween');
    }

    if (selectedComparisonType.value === 'within' && selectedStudyPeriodsCount > 1) {
        return i18n.global.t('views.monitoring.teamComparison.fields.teamsPicker.errorMessages.tooManyTeamsSelectedWithin');
    }

    return null;
});
const categoryPickerErrorMessage = computed(() => {
    if (selectedComparisonType.value === 'within') {
        const chartCategoryCount = studyPeriodCategories.value?.filter((c) => c.options.some((o) => selectedCategoryOptions.value.includes(o.id) && o.type === 'chart')).length || 0;

        if (chartCategoryCount > 3) {
            return i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.errorMessages.tooManyChartCategoriesSelected');
        }
    }

    return null;
});
const favoriteTeamsErrorMessage = computed(() => {
    if (selectedChartCategoriesCount.value > 0 && selectedStudyPeriodFavoriteIds.value.length > 1) {
        return i18n.global.t('views.monitoring.teamComparison.fields.favoriteTeams.errorMessages.tooManyFavoriteTeamsSelected');
    }

    return null;
});
const hasError = computed(() => !!teamsPickerErrorMessage.value || !!categoryPickerErrorMessage.value || !!favoriteTeamsErrorMessage.value);

const isGenerateDisabled = computed(() => {
    if (selectedAnimalGroup.value === null) {
        return true;
    }

    if (selectedCategoryOptions.value.length === 0) {
        return true;
    }

    if (selectedStudyPeriods.value.length === 0) {
        return true;
    }

    return hasError.value;
});

const isGeneratingFromSetup = ref(false);

async function handleGenerateSetup(setup: StudyPeriodSetup) {
    isGeneratingFromSetup.value = true;

    selectedAnimalGroup.value = setup.animal_group_id.toString();
    selectedCategoryOptions.value = setup.categories;
    selectedStudyPeriods.value = setup.study_periods.map((s) => s.id);
    selectedStudyPeriodFavoriteIds.value = setup.favorites.map((f) => f.id);
    selectedGrowthReferenceId.value = setup.growth_reference?.id || null;

    const sortedStudyPeriods = cloneDeep(setup.study_periods).sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    selectedFromDate.value = sortedStudyPeriods[0]?.start || subMonths(new Date(), 3).toISOString();
    selectedToDate.value = new Date().toISOString();

    await nextTick();

    generateDashboard();
}

const fetchState = ref<(StudyPeriodDashboardPayload & { fetchIndex: number }) | null>(null);

function generateDashboard() {
    fetchState.value = {
        animalGroupId: selectedAnimalGroup.value!,
        categoryIds: isGeneratingFromSetup.value ? selectedCategoryOptionsState.value : selectedCategoryOptions.value,
        studyPeriodIds: isGeneratingFromSetup.value ? selectedStudyPeriodsState.value : selectedStudyPeriods.value,
        favoriteTeamIds: isGeneratingFromSetup.value ? selectedStudyPeriodFavoriteIdsState.value : selectedStudyPeriodFavoriteIds.value,
        growthReferenceId: isGeneratingFromSetup.value ? selectedGrowthReferenceIdState.value! : selectedGrowthReferenceId.value!,
        fetchIndex: fetchState.value !== null ? fetchState.value.fetchIndex + 1 : 0,
    };

    if (isGeneratingFromSetup.value) {
        isGeneratingFromSetup.value = false;
    }
}

const queryClient = useQueryClient();

onMounted(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.getDashboardRoot });
});

const isSavedAnalysesDialogOpen = ref(false);
const isSaveAnalysisDialogOpen = ref(false);

const fetchStateSelectedChartCategoriesCount = computed(
    () => fetchState.value?.categoryIds.filter((c) => studyPeriodCategories.value?.some((c2) => c2.options.some((o) => o.id === c && o.type === 'chart'))).length || 0
);
const fetchStateSelectedTableCategoriesCount = computed(
    () => fetchState.value?.categoryIds.filter((c) => studyPeriodCategories.value?.some((c2) => c2.options.some((o) => o.id === c && o.type === 'table'))).length || 0
);

const hideChartTab = computed(() => fetchStateSelectedTableCategoriesCount.value > 0 && fetchStateSelectedChartCategoriesCount.value === 0);
const hideTableTab = computed(() => fetchStateSelectedChartCategoriesCount.value > 0 && fetchStateSelectedTableCategoriesCount.value === 0);

const chartTab = ref<InstanceType<typeof Tab> | null>(null);
const tableTab = ref<InstanceType<typeof Tab> | null>(null);

watch([hideChartTab, hideTableTab], ([newHideChartTab, newHideTableTab]) => {
    if (newHideChartTab && !newHideTableTab) {
        tableTab.value?.select?.();
    } else if (!newHideChartTab && newHideTableTab) {
        chartTab.value?.select?.();
    }
});

const teamsPickerDisplayValue = computed(() => {
    if (selectedStudyPeriods.value.length === 0) {
        return null;
    }

    if (selectedComparisonType.value === 'between') {
        return i18n.global.t('views.monitoring.teamComparison.fields.teams.texts.xOfXSelected').replace('%VALUE%', selectedStudyPeriods.value.length.toString()).replace('%MAX%', 4);
    }

    return i18n.global.t('views.monitoring.teamComparison.fields.teams.texts.xOfXSelected').replace('%VALUE%', selectedStudyPeriods.value.length.toString()).replace('%MAX%', '1');
});
const favoriteTeamsDisplayValue = computed(() => {
    if (selectedStudyPeriodFavoriteIds.value.length === 0) {
        return null;
    }

    if (selectedChartCategoriesCount.value > 0) {
        return i18n.global.t('views.monitoring.teamComparison.fields.favoriteTeams.texts.xOfXSelected').replace('%VALUE%', selectedStudyPeriodFavoriteIds.value.length.toString()).replace('%MAX%', 1);
    }

    return i18n.global
        .t('views.monitoring.teamComparison.fields.favoriteTeams.texts.xOfXSelected')
        .replace('%VALUE%', selectedStudyPeriodFavoriteIds.value.length.toString())
        .replace('%MAX%', studyPeriodFavorites.value?.length.toString() || '0');
});

onMounted(() => {
    chartTab.value?.select?.();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DashboardContentLayout, {
    class: "space-y-8",
    heading: _ctx.$t('views.monitoring.teamComparison.headings.title'),
    "has-pig-type-picker": false
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", null, [
        _createVNode(Button, {
          size: "lg",
          class: "min-w-40 px-5",
          variant: "secondary",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (isSavedAnalysesDialogOpen.value = true))
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(Plus), { class: "w-5 h-5 -ml-1" }),
            _createTextVNode(" " + _toDisplayString(_ctx.$t('views.monitoring.teamComparison.buttons.savedAnalyses')), 1)
          ]),
          _: 1
        }),
        _createElementVNode("div", _hoisted_1, [
          _createVNode(_unref(Listbox), {
            modelValue: selectedAnimalGroup.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedAnimalGroup).value = $event))
          }, {
            default: _withCtx(({ open }) => [
              _createVNode(_unref(ListboxButton), { class: "w-full min-h-[46px] flex items-center justify-between gap-x-3 bg-white border rounded-md py-2.5 px-3.5 text-left" }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("span", _hoisted_3, _toDisplayString(_ctx.$t('views.monitoring.teamComparison.fields.animalGroup.label')) + ":", 1),
                    _createElementVNode("span", _hoisted_4, _toDisplayString(animalGroupOptions.value.find((o) => o.value === selectedAnimalGroup.value)?.label || _ctx.$t('views.monitoring.teamComparison.fields.animalGroup.texts.noSelection')), 1)
                  ]),
                  _createVNode(_unref(ChevronDown), {
                    class: _normalizeClass(["flex-shrink-0 h-5 w-5 transition-transform", { 'rotate-180': open }]),
                    "aria-hidden": "true"
                  }, null, 8, ["class"])
                ]),
                _: 2
              }, 1024),
              _createVNode(_Transition, {
                "enter-active-class": "transition duration-100 ease-out",
                "enter-from-class": "transform scale-95 opacity-0",
                "enter-to-class": "transform scale-100 opacity-100",
                "leave-active-class": "transition duration-100 ease-out",
                "leave-from-class": "transform scale-100 opacity-100",
                "leave-to-class": "transform scale-95 opacity-0"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ListboxOptions), { class: "absolute z-20 mt-1 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none" }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(animalGroupOptions.value, (option) => {
                        return (_openBlock(), _createBlock(_unref(ListboxOption), {
                          key: option.value,
                          value: option.value
                        }, {
                          default: _withCtx(({ active, selected }) => [
                            _createElementVNode("li", {
                              class: _normalizeClass(["py-2 px-3 h-full truncate block w-full text-left cursor-pointer", {
                                        'bg-primary-300 bg-opacity-40': selected,
                                        'hover:bg-gray-100': !selected,
                                        'bg-gray-100': active && !selected,
                                    }])
                            }, _toDisplayString(option.label), 3)
                          ]),
                          _: 2
                        }, 1032, ["value"]))
                      }), 128))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["modelValue"])
        ]),
        _createVNode(CategoryPicker, {
          "selected-comparison-type": selectedComparisonType.value,
          "onUpdate:selectedComparisonType": _cache[2] || (_cache[2] = ($event: any) => ((selectedComparisonType).value = $event)),
          "selected-category-options": selectedCategoryOptions.value,
          "onUpdate:selectedCategoryOptions": _cache[3] || (_cache[3] = ($event: any) => ((selectedCategoryOptions).value = $event)),
          categories: _unref(studyPeriodCategories) || [],
          "error-message": categoryPickerErrorMessage.value
        }, null, 8, ["selected-comparison-type", "selected-category-options", "categories", "error-message"]),
        _createVNode(TeamsPicker, {
          "selected-study-periods": selectedStudyPeriods.value,
          "onUpdate:selectedStudyPeriods": _cache[4] || (_cache[4] = ($event: any) => ((selectedStudyPeriods).value = $event)),
          "selected-from-date": selectedFromDate.value,
          "onUpdate:selectedFromDate": _cache[5] || (_cache[5] = ($event: any) => ((selectedFromDate).value = $event)),
          "selected-to-date": selectedToDate.value,
          "onUpdate:selectedToDate": _cache[6] || (_cache[6] = ($event: any) => ((selectedToDate).value = $event)),
          "display-value": teamsPickerDisplayValue.value,
          "study-periods": _unref(studyPeriods),
          disabled: selectedAnimalGroup.value === null,
          loading: _unref(isStudyPeriodsLoading),
          "error-message": teamsPickerErrorMessage.value
        }, null, 8, ["selected-study-periods", "selected-from-date", "selected-to-date", "display-value", "study-periods", "disabled", "loading", "error-message"]),
        _createVNode(CustomMultiSelect, {
          value: (selectedStudyPeriodFavoriteIds.value as any),
          "onUpdate:value": _cache[7] || (_cache[7] = ($event: any) => (((selectedStudyPeriodFavoriteIds.value as any)) = $event)),
          items: _unref(studyPeriodFavorites) || [],
          "item-value": "id",
          "item-text": "name",
          class: _normalizeClass(["mt-3", { 'opacity-60 pointer-events-none': selectedAnimalGroup.value === null }]),
          "flat-values": "",
          disabled: !selectedAnimalGroup.value
        }, {
          trigger: _withCtx(({ displayValue, expanded }) => [
            _createElementVNode("div", {
              class: _normalizeClass(["mb-3 w-full min-h-[46px] flex items-center justify-between gap-x-3 bg-white border rounded-md py-2.5 px-3.5 text-left transition-colors", { 'border-red-500': favoriteTeamsErrorMessage.value }])
            }, [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("span", _hoisted_6, _toDisplayString(_ctx.$t('views.monitoring.teamComparison.fields.favoriteTeams.label')) + ": ", 1),
                _createElementVNode("span", _hoisted_7, _toDisplayString(favoriteTeamsDisplayValue.value || displayValue), 1)
              ]),
              (_unref(isStudyPeriodFavoritesLoading))
                ? (_openBlock(), _createBlock(Spinner, {
                    key: 0,
                    class: "flex-shrink-0 h-5 w-5"
                  }))
                : (_openBlock(), _createBlock(_unref(ChevronDown), {
                    key: 1,
                    class: _normalizeClass(["flex-shrink-0 h-5 w-5 transition-transform", { 'rotate-180': expanded }]),
                    "aria-hidden": "true"
                  }, null, 8, ["class"]))
            ], 2)
          ]),
          item: _withCtx(({ item, text }) => [
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("div", _hoisted_9, [
                _createTextVNode(_toDisplayString(text) + " ", 1),
                (item.locationNameAugmented)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_10, "(" + _toDisplayString(item.locationNameAugmented) + ")", 1))
                  : _createCommentVNode("", true)
              ]),
              (item.start && item.end)
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(_ctx.$d(_unref(parseISO)(item.start as string), 'short')) + _toDisplayString(item.end ? ` - ${_ctx.$d(_unref(parseISO)(item.end as string), 'short')}` : ''), 1))
                : _createCommentVNode("", true)
            ])
          ]),
          append: _withCtx(() => [
            _createVNode(ExpandCollapseTransition, null, {
              default: _withCtx(() => [
                (favoriteTeamsErrorMessage.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                      _createElementVNode("div", _hoisted_13, _toDisplayString(favoriteTeamsErrorMessage.value), 1)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["value", "items", "class", "disabled"]),
        _createElementVNode("div", _hoisted_14, [
          _createVNode(_unref(Listbox), {
            "model-value": selectedGrowthReferenceId.value,
            "onUpdate:modelValue": handleGrowthReferenceChange
          }, {
            default: _withCtx(({ open }) => [
              _createVNode(_unref(ListboxButton), {
                class: "w-full min-h-[46px] flex items-center justify-between gap-x-3 bg-white border rounded-md py-2.5 px-3.5 text-left disabled:opacity-60 disabled:pointer-events-none",
                disabled: selectedAnimalGroup.value === null
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_15, [
                    _createElementVNode("span", _hoisted_16, _toDisplayString(_ctx.$t('views.monitoring.teamComparison.fields.growthReference.label')) + ":", 1),
                    _createElementVNode("span", _hoisted_17, _toDisplayString(_unref(growthReferences)?.find((o) => o.id === selectedGrowthReferenceId.value)?.name || _ctx.$t('views.monitoring.teamComparison.fields.growthReference.placeholder')), 1)
                  ]),
                  (_unref(isGrowthReferencesLoading))
                    ? (_openBlock(), _createBlock(Spinner, {
                        key: 0,
                        class: "flex-shrink-0 h-5 w-5",
                        "aria-hidden": "true"
                      }))
                    : (_openBlock(), _createBlock(_unref(ChevronDown), {
                        key: 1,
                        class: _normalizeClass(["flex-shrink-0 h-5 w-5 transition-transform", { 'rotate-180': open }]),
                        "aria-hidden": "true"
                      }, null, 8, ["class"]))
                ]),
                _: 2
              }, 1032, ["disabled"]),
              _createVNode(_Transition, {
                "enter-active-class": "transition duration-100 ease-out",
                "enter-from-class": "transform scale-95 opacity-0",
                "enter-to-class": "transform scale-100 opacity-100",
                "leave-active-class": "transition duration-100 ease-out",
                "leave-from-class": "transform scale-100 opacity-100",
                "leave-to-class": "transform scale-95 opacity-0"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ListboxOptions), { class: "absolute z-20 mt-1 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none" }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(growthReferences), (option) => {
                        return (_openBlock(), _createBlock(_unref(ListboxOption), {
                          key: option.id,
                          value: option.id
                        }, {
                          default: _withCtx(({ active, selected }) => [
                            _createElementVNode("li", {
                              class: _normalizeClass(["py-2 px-3 h-full truncate block w-full text-left cursor-pointer", {
                                        'bg-primary-300 bg-opacity-40': selected,
                                        'hover:bg-gray-100': !selected,
                                        'bg-gray-100': active && !selected,
                                    }])
                            }, _toDisplayString(option.name), 3)
                          ]),
                          _: 2
                        }, 1032, ["value"]))
                      }), 128))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["model-value"])
        ]),
        _createElementVNode("div", _hoisted_18, [
          _createVNode(Button, {
            size: "lg",
            class: "min-w-40 px-5 min-h-12",
            variant: "secondary",
            disabled: isGenerateDisabled.value || isLoading.value,
            onClick: _cache[8] || (_cache[8] = ($event: any) => (isSaveAnalysisDialogOpen.value = true))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Plus), { class: "w-5 h-5 -ml-1" }),
              _createTextVNode(" " + _toDisplayString(_ctx.$t('views.monitoring.teamComparison.buttons.saveAnalysis')), 1)
            ]),
            _: 1
          }, 8, ["disabled"]),
          _createVNode(Button, {
            size: "lg",
            class: "min-w-40 px-5 min-h-12",
            disabled: isGenerateDisabled.value || isLoading.value,
            onClick: generateDashboard
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.teamComparison.buttons.generate')), 1)
            ]),
            _: 1
          }, 8, ["disabled"])
        ])
      ]),
      _createElementVNode("div", _hoisted_19, [
        _createVNode(_unref(Tabs), {
          teleport: "#team-comparison-tabs-target",
          "active-tab-class": "--active"
        }, {
          default: _withCtx(() => [
            _createElementVNode("nav", _hoisted_20, [
              (!hideChartTab.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                    _createVNode(_unref(Tab), {
                      ref_key: "chartTab",
                      ref: chartTab,
                      name: _ctx.$t('views.monitoring.teamComparison.tabs.chart')
                    }, {
                      default: _withCtx(() => [
                        _createVNode(ChartTab, { "fetch-state": fetchState.value }, null, 8, ["fetch-state"])
                      ]),
                      _: 1
                    }, 8, ["name"])
                  ]))
                : _createCommentVNode("", true),
              (!hideTableTab.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                    _createVNode(_unref(Tab), {
                      ref_key: "tableTab",
                      ref: tableTab,
                      name: _ctx.$t('views.monitoring.teamComparison.tabs.table')
                    }, {
                      default: _withCtx(() => [
                        _createVNode(TableTab, { "fetch-state": fetchState.value }, null, 8, ["fetch-state"])
                      ]),
                      _: 1
                    }, 8, ["name"])
                  ]))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        }),
        _cache[11] || (_cache[11] = _createElementVNode("div", {
          id: "team-comparison-tabs-target",
          class: "mt-6"
        }, null, -1))
      ]),
      _createVNode(SaveAnalysisDialog, {
        open: isSaveAnalysisDialogOpen.value,
        "onUpdate:open": _cache[9] || (_cache[9] = ($event: any) => ((isSaveAnalysisDialogOpen).value = $event)),
        state: saveAnalysisState.value,
        "comparison-type": selectedComparisonType.value,
        "category-names": categoryNames.value,
        "animal-group-name": animalGroupName.value,
        "study-period-names": studyPeriodNames.value,
        "favorite-team-names": favoriteTeamNames.value,
        "growth-reference-name": growthReferenceName.value
      }, null, 8, ["open", "state", "comparison-type", "category-names", "animal-group-name", "study-period-names", "favorite-team-names", "growth-reference-name"]),
      _createVNode(SavedAnalysesDialog, {
        open: isSavedAnalysesDialogOpen.value,
        "onUpdate:open": _cache[10] || (_cache[10] = ($event: any) => ((isSavedAnalysesDialogOpen).value = $event)),
        categories: _unref(studyPeriodCategories) || [],
        onGenerate: handleGenerateSetup
      }, null, 8, ["open", "categories"])
    ]),
    _: 1
  }, 8, ["heading"]))
}
}

})